import React from 'react';

// Design System Components
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Slider from 'Components/DesignSystem/Slider/Slider';
import TextField from 'Components/DesignSystem/TextField/TextField';

// Module Components
import SectionHeading from './SectionHeading';

// Constants
import {
  CTA_HEIGHT_PX_RANGE,
  CTA_WIDTH_PCT_RANGE,
  MAX_CTA_LABEL_LENGTH,
  STRINGS,
} from '../../../constants';

// Types
import { CtaConfiguration } from '../../../types';
// i18n — the slider labels carry their number as a placeholder (R70).
import { interpolate } from '../../../i18n';

interface ButtonSectionProps {
  cta: CtaConfiguration;
  /** A patch of the options — one control changes one option (spec R70). */
  onCtaOptionsChange: (options: Partial<CtaConfiguration>) => void;
}

/**
 * The Primary CTA Button's own section (spec R70): its label, what it shows, and
 * how big it is.
 *
 * The width slider is rendered **only while "Full card width" is unticked** — with
 * the button spanning the card there is no width to choose, and the prototype hides
 * it for the same reason. Its stored value survives the round trip, so unticking
 * "Full card width" brings the previous width back rather than the default.
 *
 * Each slider's caption carries its current value, which is what makes the value
 * readable and announced without pointing at the thumb (NFR A1).
 */
const ButtonSection = ({ cta, onCtaOptionsChange }: ButtonSectionProps) => (
  <div className="tw-flex tw-flex-col tw-gap-3 tw-px-3 tw-py-3 tw-border-b tw-border-edge">
    <SectionHeading as="h4">{STRINGS.pane.button}</SectionHeading>

    <TextField
      label={STRINGS.pane.ctaLabel}
      placeholder={STRINGS.pane.ctaLabelPlaceholder}
      value={cta.label}
      onChange={(label) => onCtaOptionsChange({ label })}
      maxLength={MAX_CTA_LABEL_LENGTH}
      labelSize="sm"
    />

    <div className="tw-flex tw-flex-col tw-gap-2">
      <Checkbox
        label={STRINGS.pane.ctaShowText}
        checked={cta.showText}
        onChange={(showText) => onCtaOptionsChange({ showText })}
        size="sm"
      />
      <Checkbox
        label={STRINGS.pane.ctaShowIcon}
        checked={cta.showIcon}
        onChange={(showIcon) => onCtaOptionsChange({ showIcon })}
        size="sm"
      />
      <Checkbox
        label={STRINGS.pane.ctaFullWidth}
        checked={cta.fullWidth}
        onChange={(fullWidth) => onCtaOptionsChange({ fullWidth })}
        size="sm"
      />
    </div>

    {!cta.fullWidth && (
      <Slider
        label={interpolate(STRINGS.pane.ctaWidth, { width: cta.widthPct })}
        value={cta.widthPct}
        min={CTA_WIDTH_PCT_RANGE.min}
        max={CTA_WIDTH_PCT_RANGE.max}
        step={CTA_WIDTH_PCT_RANGE.step}
        onChange={(widthPct) => onCtaOptionsChange({ widthPct })}
      />
    )}

    <Slider
      label={interpolate(STRINGS.pane.ctaHeight, { height: cta.heightPx })}
      value={cta.heightPx}
      min={CTA_HEIGHT_PX_RANGE.min}
      max={CTA_HEIGHT_PX_RANGE.max}
      step={CTA_HEIGHT_PX_RANGE.step}
      onChange={(heightPx) => onCtaOptionsChange({ heightPx })}
    />
  </div>
);

export default ButtonSection;
